export const MASTER_NUMBERS = new Set([11, 22, 33]);

export const LIFE_PATHS = {
  1: {
    title: 'The Leader',
    keywords: ['Independence', 'Drive', 'Originality', 'Courage'],
    summary: 'Life path 1 is the number of beginnings. You are here to stand on your own feet, trust your own judgment, and go first where others hesitate. People often look to you to make the call, even when you never asked to be in charge.',
    strengths: 'Self-starting, decisive, and inventive. You can turn an idea into a plan faster than most, and you are at your best when you have room to do things your own way.',
    challenges: 'Impatience, stubbornness, and a habit of carrying everything alone. Your growth is learning that asking for help does not make you less capable.',
    love: 'You need a partner who respects your independence and has a life of their own. You love with loyalty and protectiveness, and you do better with someone who will tell you the truth than with someone who simply follows.',
    work: 'You thrive where you can lead or build something new: running a business, heading a team, freelancing, or any role where initiative is rewarded.',
    compatible: [3, 5, 6],
  },
  2: {
    title: 'The Peacemaker',
    keywords: ['Harmony', 'Sensitivity', 'Partnership', 'Diplomacy'],
    summary: 'Life path 2 is the number of connection. You are here to bring people together, read what is not being said, and find the path where everyone can win. Your strength is quiet, and it holds a lot together.',
    strengths: 'Intuitive, patient, tactful, and deeply caring. You notice the mood in a room before anyone speaks, and you are a gifted listener and mediator.',
    challenges: 'Over-giving, avoiding conflict, and losing yourself in other people\'s needs. Your growth is learning that your own voice matters as much as the peace.',
    love: 'Partnership is central for you. You love tenderly and attentively, and you need someone who notices your care and returns it. Gentle honesty works better for you than drama.',
    work: 'You shine in roles built on cooperation: counseling, teaching, human resources, healthcare, design, or being the trusted right hand behind a leader.',
    compatible: [4, 6, 8, 9],
  },
  3: {
    title: 'The Communicator',
    keywords: ['Expression', 'Creativity', 'Joy', 'Social energy'],
    summary: 'Life path 3 is the number of self-expression. You are here to create, speak, write, perform, and lift the mood of everyone around you. Your joy is contagious, and it is part of your purpose, not a distraction from it.',
    strengths: 'Imaginative, witty, warm, and expressive. You turn feelings into words, art, or laughter, and people remember how you made them feel.',
    challenges: 'Scattered energy, unfinished projects, and hiding hurt behind humor. Your growth is choosing a few things and seeing them all the way through.',
    love: 'You want fun, conversation, and affection. You do best with a partner who enjoys your spark and gives you room to shine, and who you can be honest with on the days you do not feel bright.',
    work: 'You fit creative and people-facing work: writing, marketing, performing, teaching, design, social media, or anything where ideas and communication matter.',
    compatible: [1, 5, 6, 9],
  },
  4: {
    title: 'The Builder',
    keywords: ['Stability', 'Discipline', 'Loyalty', 'Hard work'],
    summary: 'Life path 4 is the number of foundations. You are here to build things that last: a home, a career, a family, a system that works. People trust you because you do what you say you will do.',
    strengths: 'Reliable, practical, organized, and honest. You have patience for the steady work that others skip, and your results show it.',
    challenges: 'Rigidity, overwork, and worry when plans change. Your growth is trusting that flexibility will not undo what you have built.',
    love: 'You love through commitment and action: showing up, fixing things, planning a future. You need a partner who values loyalty and stability and who can gently pull you away from work to enjoy life.',
    work: 'You excel in roles that need structure and follow-through: engineering, finance, project management, construction, law, operations, or running a steady business.',
    compatible: [2, 6, 7, 8],
  },
  5: {
    title: 'The Free Spirit',
    keywords: ['Freedom', 'Adventure', 'Change', 'Curiosity'],
    summary: 'Life path 5 is the number of freedom. You are here to explore, experience, and learn through change. Routine drains you; new places, people, and ideas bring you alive.',
    strengths: 'Adaptable, adventurous, magnetic, and quick to learn. You handle change better than almost anyone, and you bring energy to everything you touch.',
    challenges: 'Restlessness, impulsiveness, and running from commitment when it starts to feel like a cage. Your growth is discovering that freedom and devotion can live together.',
    love: 'You need excitement and trust in equal measure. The right partner gives you room to roam and grow, and joins you on the adventure instead of trying to hold you still.',
    work: 'You thrive with variety and movement: travel, sales, media, entrepreneurship, events, or any job where no two days are the same.',
    compatible: [1, 3, 7],
  },
  6: {
    title: 'The Nurturer',
    keywords: ['Love', 'Responsibility', 'Care', 'Home'],
    summary: 'Life path 6 is the number of love and responsibility. You are here to care for people, create beauty and comfort, and make the places you belong to feel like home. Others come to you when they need support.',
    strengths: 'Compassionate, devoted, protective, and generous. You have a gift for healing and for making people feel safe and seen.',
    challenges: 'Over-responsibility, perfectionism, and giving until you are empty. Your growth is caring for yourself with the same devotion you give everyone else.',
    love: 'Love and family are at the heart of your path. You are a loyal, giving partner and you need someone who appreciates your care and gives it back, rather than someone who needs to be rescued.',
    work: 'You fit caring and creative roles: healthcare, teaching, counseling, interior design, hospitality, or community work.',
    compatible: [1, 2, 3, 4, 9],
  },
  7: {
    title: 'The Seeker',
    keywords: ['Wisdom', 'Introspection', 'Intuition', 'Truth'],
    summary: 'Life path 7 is the number of the inner search. You are here to ask deep questions, study, reflect, and trust your intuition. You need time alone to think, and it is where your best insights come from.',
    strengths: 'Analytical, perceptive, spiritual, and wise beyond your years. You see beneath the surface and you are rarely fooled for long.',
    challenges: 'Isolation, skepticism, and keeping people at a distance. Your growth is letting others in, even before you have everything figured out.',
    love: 'You need a partner who respects your privacy and can meet you in deep conversation. Trust builds slowly for you, and once it is there, your loyalty runs very deep.',
    work: 'You excel in work that rewards depth: research, science, technology, writing, psychology, spiritual practice, or any field where expertise matters.',
    compatible: [4, 5],
  },
  8: {
    title: 'The Powerhouse',
    keywords: ['Ambition', 'Abundance', 'Authority', 'Resilience'],
    summary: 'Life path 8 is the number of power and achievement. You are here to master the material world: money, influence, and the ability to make big things happen. Your path asks you to use that power well.',
    strengths: 'Ambitious, confident, strategic, and resilient. You see the big picture, handle pressure, and recover from setbacks stronger than before.',
    challenges: 'Workaholism, control, and measuring your worth by success. Your growth is balancing achievement with rest, generosity, and connection.',
    love: 'You are protective and generous, and you want a partner who is strong in their own right. Respect is essential to you, and so is learning to let your guard down at home.',
    work: 'You are built for leadership and results: business, finance, law, real estate, management, or building your own company.',
    compatible: [2, 4, 6],
  },
  9: {
    title: 'The Humanitarian',
    keywords: ['Compassion', 'Idealism', 'Wisdom', 'Completion'],
    summary: 'Life path 9 is the number of completion and service. You carry something of every number before it, and you are here to give back: to share your wisdom, creativity, and compassion with the wider world.',
    strengths: 'Generous, idealistic, creative, and tolerant. You see the best in people and you care about things bigger than yourself.',
    challenges: 'Holding onto the past, martyrdom, and difficulty with endings. Your growth is letting go gracefully so something new can begin.',
    love: 'You love broadly and deeply. You need a partner who shares your values and supports your bigger purpose, and with whom you can be cared for, not only caring.',
    work: 'You fit purpose-driven work: nonprofits, the arts, healing professions, teaching, activism, or anything that serves the greater good.',
    compatible: [2, 3, 6],
  },
  11: {
    title: 'The Intuitive',
    keywords: ['Inspiration', 'Intuition', 'Sensitivity', 'Vision'],
    summary: 'Life path 11 is the first master number. It carries the gentleness of the 2 with a much stronger charge: heightened intuition, vision, and the ability to inspire others. You are here to be a light for people.',
    strengths: 'Deeply intuitive, inspiring, empathic, and spiritually aware. You often know things before you can explain how.',
    challenges: 'Nervous energy, self-doubt, and feeling overwhelmed by your own sensitivity. Your growth is grounding yourself so your gifts can flow without burning you out.',
    love: 'You need a partner who understands your depth and sensitivity and offers steady, calm support. Emotional honesty is essential for you.',
    work: 'You are drawn to inspiring and healing roles: teaching, counseling, the arts, spiritual work, or leading a cause you believe in.',
    compatible: [2, 4, 6, 22],
  },
  22: {
    title: 'The Master Builder',
    keywords: ['Vision', 'Mastery', 'Legacy', 'Practical power'],
    summary: 'Life path 22 is the master builder. It joins the vision of the 11 with the practical strength of the 4. You are here to turn large dreams into real structures that help many people.',
    strengths: 'Visionary, disciplined, capable, and grounded. You can see what is possible and you have the patience to build it brick by brick.',
    challenges: 'Enormous pressure, fear of failing your own potential, and overwork. Your growth is taking one step at a time and letting the work be human-sized.',
    love: 'You need a partner who believes in your vision and also reminds you to live in the present. Stability at home gives you the strength to build in the world.',
    work: 'You are suited to work at scale: architecture, engineering, founding organizations, policy, or leadership roles that shape communities.',
    compatible: [4, 6, 8, 11],
  },
  33: {
    title: 'The Master Teacher',
    keywords: ['Compassion', 'Healing', 'Guidance', 'Devotion'],
    summary: 'Life path 33 is the rarest master number. It carries the loving care of the 6 at a higher pitch. You are here to heal, teach, and uplift, often through simple everyday kindness.',
    strengths: 'Selfless, nurturing, wise, and inspiring. People feel safe with you, and you have a gift for helping them grow.',
    challenges: 'Self-sacrifice, taking on other people\'s pain, and impossibly high standards. Your growth is accepting that you deserve the same compassion you offer.',
    love: 'You love with devotion and you need a partner who supports your calling and makes sure you are cared for too.',
    work: 'You thrive in teaching, healing, counseling, caregiving, the arts, and spiritual guidance.',
    compatible: [6, 9, 11],
  },
};

function digitSum(value) {
  return String(value).split('').reduce((total, digit) => total + Number(digit), 0);
}

function reduceSteps(value, keepMasters) {
  const steps = [value];
  let current = value;
  while (current > 9 && !(keepMasters && MASTER_NUMBERS.has(current))) {
    current = digitSum(current);
    steps.push(current);
  }
  return steps;
}

function describe(label, value, steps) {
  if (steps.length === 1) return `${label} ${value} → ${value}`;
  const parts = [];
  let previous = value;
  steps.slice(1).forEach((step) => {
    parts.push(`${String(previous).split('').join(' + ')} = ${step}`);
    previous = step;
  });
  return `${label} ${value} → ${parts.join(' → ')}`;
}

export function lifePathFor(year, month, day) {
  const monthSteps = reduceSteps(month, true);
  const daySteps = reduceSteps(day, true);
  const yearSteps = reduceSteps(digitSum(year), true);
  const monthValue = monthSteps[monthSteps.length - 1];
  const dayValue = daySteps[daySteps.length - 1];
  const yearValue = yearSteps[yearSteps.length - 1];
  const total = monthValue + dayValue + yearValue;
  const totalSteps = reduceSteps(total, true);
  const number = totalSteps[totalSteps.length - 1];
  const yearLine = `Year ${year} → ${String(year).split('').join(' + ')} = ${digitSum(year)}`
    + (yearSteps.length > 1 ? ` → ${yearSteps.slice(1).map((step, index) => `${String(yearSteps[index]).split('').join(' + ')} = ${step}`).join(' → ')}` : '');
  const lines = [
    describe('Month', month, monthSteps),
    describe('Day', day, daySteps),
    yearLine,
    `${monthValue} + ${dayValue} + ${yearValue} = ${total}`
      + (totalSteps.length > 1 ? ` → ${totalSteps.slice(1).map((step, index) => `${String(totalSteps[index]).split('').join(' + ')} = ${step}`).join(' → ')}` : ''),
    `Life path ${number}`,
  ];
  return { number, lines };
}
